import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { first, reduce, keys, noop } from 'lodash';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';

import { googleAutocompleteSelector } from 'store/selectors/search';
import { on } from 'utils/dom/dom';
import { Input } from 'components/core/Input';
import GoogleMapsAutocomplete from 'lib/googleMaps/autocomplete';
import './GoogleAutoComplete.scss';
import mapPin from 'assets/images/icons/map-pin.svg';
import { Image } from 'components/core/Image';
import { Paragraph } from 'components/core/Typography';
import Info from 'assets/images/icons/info.svg';
import { withExperiment } from '@sparefoot/react-core/decorators';

const mapStateToProps = (state) => ({
	googleMapsKey: googleAutocompleteSelector(state).googleMapsKey,
	geolocation: state.user.geolocation,
	bucket: state.experiments.bucket,
});
export const enhance = compose(
	connect(mapStateToProps),
	withExperiment('sf_15342_geolocation_sfdc')
);

export function formatPlaceObject(place) {
	const params = {};

	if (place) {
		const addressComponents = reduce(
			place.address_components,
			(components, c) => ({
				...components,
				[first(c.types)]: c.short_name,
			}),
			{}
		);

		params.city = addressComponents.locality;
		params.zip = addressComponents.postal_code;
		params.state = addressComponents.administrative_area_level_1;
		params.locationSource = 'google_autocomplete';

		if (place.geometry) {
			params.latitude = place.geometry.location.lat();
			params.longitude = place.geometry.location.lng();
		}
	}
	return params;
}

// TODO: recompose
export class GoogleAutocomplete extends PureComponent {
	static propTypes = {
		/** func onChange({location, city, zip, state, locationSource}) */
		onChange: PropTypes.func,
		value: PropTypes.string,
		getRef: PropTypes.func,
		googleMapsKey: PropTypes.string,
		hideOnScroll: PropTypes.bool,
		label: PropTypes.string,
	};

	static defaultProps = {
		onChange: noop,
		value: '',
		hideOnScroll: false,
	};

	constructor(props) {
		super(props);
		this.state = {
			value: props.value,
			loadedAutocomplete: false,
			showCustomDropdown: false,
			errorMessage: '',
		};
	}

	componentDidMount() {
		this.observer = new MutationObserver((mutationsList) => {
			const GoogleAutocompleteDrawerEl =
				document.querySelector('.pac-container');
			if (GoogleAutocompleteDrawerEl) {
				if (this.props.sf15342Variation === 'variation1') {
					GoogleAutocompleteDrawerEl.classList.add(
						'pac-container-variation1'
					);
					GoogleAutocompleteDrawerEl.classList.remove(
						'pac-container-default'
					);
					GoogleAutocompleteDrawerEl.style.marginTop = '23px';
				} else {
					GoogleAutocompleteDrawerEl.classList.add(
						'pac-container-default'
					);
					GoogleAutocompleteDrawerEl.classList.remove(
						'pac-container-variation1'
					);
					GoogleAutocompleteDrawerEl.style.marginTop = '-5px';
				}
			}
		});

		this.observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		if (this.props.hideOnScroll) {
			this.scrollHandler = (e) => {
				e.preventDefault();
				const GoogleAutocompleteDrawerEl =
					document.querySelector('.pac-container');
				if (
					GoogleAutocompleteDrawerEl &&
					GoogleAutocompleteDrawerEl.style.display !== 'none' &&
					this.textInput === document.activeElement
				) {
					GoogleAutocompleteDrawerEl.style.display = 'none';
				}
			};

			window.addEventListener('scroll', this.scrollHandler, {
				passive: false,
			});
		}
	}

	componentWillUnmount() {
		if (this.observer) {
			this.observer.disconnect();
		}

		if (this.props.hideOnScroll && this.scrollHandler) {
			window.removeEventListener('scroll', this.scrollHandler);
		}

		GoogleMapsAutocomplete.removeAutocomplete(this.autoComplete);
		this.autoComplete = null;
	}

	_onFocus = () => {
		if (!this.state.loadedAutocomplete) {
			GoogleMapsAutocomplete.configureAutocomplete(
				this.textInput,
				this._onAutocomplete,
				this.props.googleMapsKey
			).then((autoComplete) => {
				this.autoComplete = autoComplete;
			});
			this.setState({ loadedAutocomplete: true });
		}

		this.setState({
			showCustomDropdown: true,
			errorMessage: '',
		});
	};

	_onBlur = () => {
		setTimeout(
			() =>
				this.setState({
					showCustomDropdown: false,
				}),
			200
		);
	};

	onCustomOptionClick = (geolocation) => () => {
		const location = geolocation.city + ', ' + geolocation.region;
		this.setState({
			value: 'Detecting location...',
			showCustomDropdown: false,
			errorMessage: '',
		});
		if (geolocation) {
			this.props.onChange({
				location: location || 'Current Location',
			});
		} else {
			this.setState({
				value: '',
				errorMessage:
					'We weren’t able to get your location. Please type it in manually.',
			});
		}
	};

	_onChange = (evt) => {
		const { value } = evt.target;

		this.setState({ value });
		this.props.onChange({ location: value });
	};

	_onAutocomplete = (value, place) => {
		this.setState({ value });

		const locationParams = formatPlaceObject(place);
		this.props.onChange({
			...locationParams,
			location: value,
		});
	};

	_buildProps() {
		return {
			...this.props,
			getRef: (ref) => {
				this.textInput = ref;
				if (this.props.getRef) {
					this.props.getRef(ref);
				}
			},
			onChange: this._onChange,
			onFocus: this._onFocus,
			onBlur: this._onBlur,
		};
	}

	render() {
		const { showCustomDropdown, errorMessage } = this.state;
		const { sf15342Variation } = this.props;
		return sf15342Variation === 'variation1' ? (
			<div className="google-autocomplete">
				<Input
					placeholder=""
					value={this.state.value}
					{...this._buildProps()}
				/>

				{showCustomDropdown && (
					<div
						className="custom-dropdown"
						onMouseDown={this.onCustomOptionClick(
							this.props.geolocation
						)}
					>
						<Image
							src={mapPin}
							alt="Use my location"
							className="map-pin"
						/>
						Use My Location
					</div>
				)}
				{errorMessage && (
					<div className="error-message">
						<Image
							src={Info}
							alt="Info"
							className="info-icon"
						/>
						<Paragraph last>
							{
								'We weren’t able to get your location. Please type it in manually.'
							}
						</Paragraph>
					</div>
				)}
			</div>
		) : (
			<div className="google-autocomplete">
				<Input
					placeholder=""
					value={this.state.value}
					{...this._buildProps()}
				/>
			</div>
		);
	}
}

export default enhance(GoogleAutocomplete);
